import React from "react";
import {
  Building2,
  BadgeEuro,
  Plug,
  UtensilsCrossed,
  BedDouble,
  Coffee,
} from "lucide-react";
import { HERO_IMAGE, HERO_CATEGORIES, HERO_STATS } from "../../content";

const STAT_ICONS = { Building2, BadgeEuro, Plug };
const CAT_ICONS = { UtensilsCrossed, BedDouble, Coffee };

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const StatCard = ({ value, label, icon, testid }) => {
  const Icon = STAT_ICONS[icon];
  return (
    <div
      data-testid={testid}
      className="flex flex-col gap-3 bg-white/5 backdrop-blur-sm border border-white/15 rounded-2xl p-5"
    >
      {Icon && (
        <span className="w-9 h-9 rounded-lg bg-white/10 text-[#88d2e7] flex items-center justify-center">
          <Icon size={18} strokeWidth={2} />
        </span>
      )}
      <span className="text-white text-2xl md:text-3xl font-black tracking-tight leading-none">
        {value}
      </span>
      <span className="text-white/75 text-xs md:text-sm font-medium leading-snug">
        {label}
      </span>
    </div>
  );
};

const CategoryChip = ({ label, icon, testid }) => {
  const Icon = CAT_ICONS[icon];
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-2 bg-white/8 border border-white/20 backdrop-blur-sm text-white/95 text-xs md:text-sm font-semibold tracking-wide rounded-full px-4 py-2"
    >
      {Icon && <Icon size={15} strokeWidth={2} className="text-[#88d2e7]" />}
      {label}
    </span>
  );
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 md:pt-32 pb-20 md:pb-24"
      style={{
        background:
          "linear-gradient(135deg, #003242 0%, #005870 55%, #0095b4 100%)",
      }}
    >
      <div className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full bg-[#88d2e7]/15 blur-3xl pointer-events-none" />
      <div className="absolute -left-32 bottom-0 w-[400px] h-[400px] rounded-full bg-[#005870]/30 blur-3xl pointer-events-none" />

      <div className="met-container relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7">
          <p
            data-testid="hero-eyebrow"
            className="text-white/85 text-sm md:text-base font-medium mb-4"
          >
            Le fournisseur dédié aux entreprises du secteur
          </p>

          <div className="flex flex-wrap gap-2 mb-7" data-testid="hero-categories">
            {HERO_CATEGORIES.map((c, i) => (
              <CategoryChip
                key={c.label}
                label={c.label}
                icon={c.icon}
                testid={`hero-category-${i}`}
              />
            ))}
          </div>

          <h1
            data-testid="hero-headline"
            className="text-white font-black tracking-tighter leading-[1.05] text-balance text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem]"
          >
            Votre contrat énergie
            <br className="hidden md:block" />
            <span className="text-[#88d2e7]"> vous coûte peut-être </span>
            plus cher que nécessaire.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-white/85 text-base md:text-lg font-medium leading-relaxed max-w-xl"
          >
            Faites auditer gratuitement votre facture électricité ou gaz par un expert MET :
            <span className="text-white font-semibold"> sans engagement et sans surprise.</span>
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={scrollTo("lead-form")}
              data-testid="hero-audit-cta"
              className="group inline-flex items-center justify-center gap-2 bg-white text-[#005870] hover:bg-[#88d2e7] hover:text-[#003242] font-bold text-base px-8 py-4 rounded-full transition-colors duration-300"
            >
              Demander mon audit gratuit
              <svg className="transition-transform group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6"/>
              </svg>
            </button>
            <button
              type="button"
              onClick={scrollTo("lead-form")}
              data-testid="hero-callback-cta"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 hover:border-white hover:bg-white/10 font-bold text-base px-8 py-4 rounded-full transition-colors duration-300"
            >
              Me faire rappeler par un expert
            </button>
          </div>

          <div
            data-testid="hero-trust-badges"
            className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 max-w-2xl"
          >
            {HERO_STATS.map((s, i) => (
              <StatCard
                key={i}
                value={s.value}
                label={s.label}
                icon={s.icon}
                testid={`hero-trust-badge-${i}`}
              />
            ))}
          </div>
        </div>

        {/* RIGHT — framed photo */}
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-[#88d2e7]/25 to-[#005870]/25 blur-2xl pointer-events-none" />
            <div className="relative rounded-2xl overflow-hidden border border-white/15 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] aspect-[4/5]">
              <img
                src={HERO_IMAGE}
                alt="Professionnelle CHR souriante avec sa tablette"
                data-testid="hero-image"
                className="w-full h-full object-cover"
                style={{ objectPosition: "50% 50%" }}
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#003242]/40 to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
